import type { Session, User } from '@supabase/supabase-js'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { isSupabaseConfigured, supabase } from '../lib/supabase'

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
  isDemo: boolean
  signIn(email: string, password: string, captchaToken?: string): Promise<string | null>
  signUp(email: string, password: string, username: string, displayName: string, captchaToken?: string): Promise<string | null>
  signInWithGoogle(): Promise<string | null>
  requestPasswordReset(email: string, captchaToken?: string): Promise<string | null>
  updatePassword(password: string): Promise<string | null>
  signOut(): Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function makeDemoUser(email = 'gustavocortesbraga@gmail.com') {
  return {
    id: 'demo-admin',
    app_metadata: { provider: 'demo', providers: ['demo'] },
    user_metadata: { username: 'uhhvaimorrer', display_name: 'Gustavo Braga' },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
    email,
    role: 'authenticated'
  } as User
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [demoUser, setDemoUser] = useState<User | null>(() => (isSupabaseConfigured ? null : makeDemoUser()))
  const [loading, setLoading] = useState(isSupabaseConfigured)
  const [databaseAdmin, setDatabaseAdmin] = useState(false)

  useEffect(() => {
    if (!supabase) return

    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setLoading(false)
    })

    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!supabase || !session?.user) {
      setDatabaseAdmin(false)
      return
    }
    void supabase.rpc('is_admin').then(({ data }) => setDatabaseAdmin(data === true))
  }, [session?.user])

  const signIn = useCallback(async (email: string, password: string, captchaToken?: string) => {
    if (!supabase) {
      setDemoUser(makeDemoUser(email))
      return null
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password, options: captchaToken ? { captchaToken } : undefined })
    return error?.message ?? null
  }, [])

  const signUp = useCallback(async (email: string, password: string, username: string, displayName: string, captchaToken?: string) => {
    if (!supabase) {
      setDemoUser({ ...makeDemoUser(email), user_metadata: { username, display_name: displayName } })
      return null
    }
    const redirectTo = `${window.location.origin}/auth/callback`
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectTo, data: { username, display_name: displayName }, captchaToken }
    })
    return error?.message ?? null
  }, [])

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) {
      setDemoUser(makeDemoUser())
      return null
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback`, scopes: 'openid email profile' }
    })
    return error?.message ?? null
  }, [])

  const requestPasswordReset = useCallback(async (email: string, captchaToken?: string) => {
    if (!supabase) return null
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/redefinir-senha`,
      captchaToken
    })
    return error?.message ?? null
  }, [])

  const updatePassword = useCallback(async (password: string) => {
    if (!supabase) return null
    const { error } = await supabase.auth.updateUser({ password })
    return error?.message ?? null
  }, [])

  const signOut = useCallback(async () => {
    if (!supabase) {
      setDemoUser(null)
      return
    }
    await supabase.auth.signOut()
  }, [])

  const user = session?.user ?? demoUser
  const isAdmin = isSupabaseConfigured
    ? databaseAdmin
    : Boolean(user?.email?.toLowerCase() === 'gustavocortesbraga@gmail.com' && user.id === 'demo-admin')

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      session,
      loading,
      isAdmin,
      isDemo: !isSupabaseConfigured,
      signIn,
      signUp,
      signInWithGoogle,
      requestPasswordReset,
      updatePassword,
      signOut
    }),
    [user, session, loading, isAdmin, signIn, signUp, signInWithGoogle, requestPasswordReset, updatePassword, signOut]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider')
  return context
}
